import {
	type FeatureId,
	type InTrialFeatureGatesDataSource,
	type ITFGAnalyticsCallbackParams,
} from '../../common/types';
import { useGetInTrialFeatureGateBannerData } from '../use-get-user-tenant-experience-data';

import { type IsValidAudienceData } from './types';

type UseIsValidAudienceProps = {
	featureId: FeatureId;
	sendOperationalEvent: (params: ITFGAnalyticsCallbackParams) => void;
	skip?: boolean;
	tenantId: string;
} & InTrialFeatureGatesDataSource;

export const useIsValidAudience = ({
	// Apps have different ways of getting the AGG client. Its not always as simple as useClient() and depends on the app's architecture.
	// Therefore, we need to pass it in as a prop.
	tenantId,
	sendOperationalEvent,
	featureId,
	skip = false,
	...props
}: UseIsValidAudienceProps): IsValidAudienceData => {
	const { error, hasPaymentDetailsOnFile, canUserPayAndUpgrade, daysLeftInTrial, loading } =
		useGetInTrialFeatureGateBannerData({
			sendOperationalEvent,
			skip,
			tenantId,
			featureId,
			...props,
		});

	if (error || skip) {
		return {
			isValidAudience: false,
			data: {
				hasPaymentDetailsOnFile: false,
				canUserPayAndUpgrade: false,
				daysLeftInTrial: undefined,
			},
			isLoading: false,
		};
	}

	const isValidAudience =
		daysLeftInTrial !== undefined &&
		daysLeftInTrial > 0 &&
		!Boolean(hasPaymentDetailsOnFile) &&
		Boolean(canUserPayAndUpgrade);

	return {
		isValidAudience,
		// Returning the same data as useGetInTrialFeatureGateBannerData to allow us to leverage the same data for exposure analytics
		data: {
			hasPaymentDetailsOnFile,
			canUserPayAndUpgrade,
			daysLeftInTrial,
		},
		isLoading: loading,
	};
};
